/** A small router on the History API: enough for a handful of screens. */
import { type AnchorHTMLAttributes, type MouseEvent, useSyncExternalStore } from "react";

const listeners = new Set<() => void>();
const notify = () => {
  for (const listener of listeners) listener();
};

if (typeof window !== "undefined") window.addEventListener("popstate", notify);

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const snapshot = () => `${window.location.pathname}${window.location.search}`;

/** How deep into the app's own history this entry is (0: where the app was opened). */
const depth = (): number => (window.history.state as { depth?: number } | null)?.depth ?? 0;

export function navigate(to: string, options: { replace?: boolean } = {}) {
  if (to === snapshot()) return;
  if (options.replace) window.history.replaceState({ depth: depth() }, "", to);
  else window.history.pushState({ depth: depth() + 1 }, "", to);
  window.scrollTo(0, 0);
  notify();
}

/** Back if the app got here itself, otherwise to `fallback` (e.g. opened from a link). */
export function goBack(fallback = "/") {
  if (depth() > 0) window.history.back();
  else navigate(fallback, { replace: true });
}

export function useLocation(): { path: string; query: URLSearchParams } {
  const current = useSyncExternalStore(subscribe, snapshot, () => "/");
  const [path, search = ""] = current.split("?");
  return { path: path ?? "/", query: new URLSearchParams(search) };
}

/** Matches "/bookings/:id" against a path; returns the params or null. */
export function match(pattern: string, path: string): Record<string, string> | null {
  const want = pattern.split("/").filter(Boolean);
  const have = path.split("/").filter(Boolean);
  if (want.length !== have.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < want.length; i++) {
    const segment = want[i]!;
    if (segment.startsWith(":")) params[segment.slice(1)] = decodeURIComponent(have[i]!);
    else if (segment !== have[i]) return null;
  }
  return params;
}

export function Link({
  to,
  replace,
  onClick,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & { to: string; replace?: boolean }) {
  return (
    <a
      {...props}
      href={to}
      onClick={(event: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(event);
        if (event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        navigate(to, { replace });
      }}
    />
  );
}
